import { type ReactElement } from 'react'
import lazyWithRetry from '@/shared/routes/lazyWithRetry'
import { Navigate, createBrowserRouter, useNavigate } from 'react-router-dom'
import { Result } from 'antd'
import { selectIsAuthorized } from '@/entities/session'
import { RoutesPath } from '@/shared/types/RoutesPath'

import { useAppSelector } from '@/shared/types'

import { layoutWithSidebar } from '../layouts/layoutWithSidebar'
import { layoutLogin } from '../layouts/layoutLogin'
import {
  selectIsAuthLoading,
  selectUserMe,
} from '@/entities/session/model/slice'
import { UserRoles } from '@/entities/user/model/types'
import { Button } from '@/shared/ui/Button/Button'
import { ErrorPage } from '@/shared/ui/ErrorPage/ErrorPage'

//pages
const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage/LoginPage'))

const UsersPage = lazyWithRetry(() => import('@/pages/UsersPage/UsersPage'))
const InventoryPage = lazyWithRetry(
  () => import('@/pages/InventoryPage/InventoryPage'),
)

const WorkOrderListPage = lazyWithRetry(
  () => import('@/pages/WorkOrderListPage/WorkOrderListPage'),
)

const WorkOrderPage = lazyWithRetry(
  () => import('@/pages/WorkOrderPage/WorkOrderPage'),
)

const InventoryHistoryPage = lazyWithRetry(
  () => import('@/pages/InventoryHistoryPage/InventoryHistoryPage'),
)

type GuestGuardProps = {
  children: ReactElement
}

function GuestGuard({ children }: GuestGuardProps) {
  const isAuthorized = useAppSelector(selectIsAuthorized)

  if (!isAuthorized) return <Navigate to='/login' />

  return children
}

type AuthGuardProps = {
  children: ReactElement
}

function AuthGuard({ children }: AuthGuardProps) {
  const isAuthorized = useAppSelector(selectIsAuthorized)

  if (isAuthorized) return <Navigate to={RoutesPath.WORK_ORDER} />

  return children
}

type RoleGuardProps = {
  children: ReactElement
  allowedRoles: UserRoles[]
  redirectPath?: string
}

function RoleGuard({
  children,
  allowedRoles,
  redirectPath = RoutesPath.WORK_ORDER,
}: RoleGuardProps) {
  const user = useAppSelector(selectUserMe)
  const loading = useAppSelector(selectIsAuthLoading)
  const navigate = useNavigate()

  const onClick = () => {
    navigate(redirectPath)
  }

  const isPageVisible = !user || !allowedRoles.includes(user.role_key)

  if (loading) return

  if (isPageVisible) {
    return (
      <Result
        status='403'
        title='403'
        subTitle='Sorry, you are not authorized to access this page.'
        extra={
          <Button
            isLoading={false}
            onClick={onClick}
            type='primary'
            text='Back Home'
          />
        }
      />
    )
  }

  return children
}

export const appRouter = () =>
  createBrowserRouter([
    {
      element: layoutLogin,
      errorElement: <ErrorPage />,
      children: [
        {
          path: RoutesPath.LOGIN,
          element: (
            <AuthGuard>
              <LoginPage />
            </AuthGuard>
          ),
        },
      ],
    },

    {
      element: layoutWithSidebar,
      errorElement: <ErrorPage />,
      children: [
        {
          path: RoutesPath.USRES,
          element: (
            <GuestGuard>
              <RoleGuard
                allowedRoles={[
                  UserRoles.SUPER_ADMIN,
                  UserRoles.OPERATIONS_MANAGER,
                ]}
              >
                <UsersPage />
              </RoleGuard>
            </GuestGuard>
          ),
        },
      ],
    },
    {
      element: layoutWithSidebar,
      errorElement: <ErrorPage />,
      children: [
        {
          path: RoutesPath.WORK_ORDER,
          element: (
            <GuestGuard>
              <RoleGuard
                allowedRoles={[
                  UserRoles.SUPER_ADMIN,
                  UserRoles.OPERATIONS_MANAGER,
                  UserRoles.PROJECT_MANAGER,
                  UserRoles.WEREHOUSE_MANAGER,
                ]}
              >
                <WorkOrderListPage />
              </RoleGuard>
            </GuestGuard>
          ),
        },
      ],
    },
    {
      element: layoutWithSidebar,
      errorElement: <ErrorPage />,
      children: [
        {
          path: RoutesPath.INVENTORY_HISTORY,
          element: (
            <GuestGuard>
              <RoleGuard
                allowedRoles={[
                  UserRoles.SUPER_ADMIN,
                  UserRoles.OPERATIONS_MANAGER,
                  UserRoles.WEREHOUSE_MANAGER,
                  UserRoles.PROJECT_MANAGER,
                ]}
              >
                <InventoryHistoryPage />
              </RoleGuard>
            </GuestGuard>
          ),
        },
      ],
    },
    {
      element: layoutWithSidebar,
      errorElement: <ErrorPage />,
      children: [
        {
          path: RoutesPath.INVENTORY,
          element: (
            <GuestGuard>
              <RoleGuard
                allowedRoles={[
                  UserRoles.SUPER_ADMIN,
                  UserRoles.OPERATIONS_MANAGER,
                  UserRoles.WEREHOUSE_MANAGER,
                ]}
              >
                <InventoryPage />
              </RoleGuard>
            </GuestGuard>
          ),
        },
      ],
    },

    {
      element: layoutWithSidebar,
      errorElement: <ErrorPage />,
      children: [
        {
          path: RoutesPath.WORK_ORDER_NEW,
          element: (
            <GuestGuard>
              <RoleGuard
                allowedRoles={[
                  UserRoles.SUPER_ADMIN,
                  UserRoles.OPERATIONS_MANAGER,
                  UserRoles.PROJECT_MANAGER,
                  UserRoles.WEREHOUSE_MANAGER,
                ]}
              >
                <WorkOrderPage />
              </RoleGuard>
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: '*',
      element: <Navigate to={RoutesPath.WORK_ORDER} />,
    },
  ])
